/**
 * Nigerian region vocabulary — the source for a dish's `region_tag`.
 *
 * `region_tag` is not decoration: the backend refuses to publish a dish
 * without one (menu service, "dish_region_required"), and a dish created
 * without it is inserted as `hidden`, invisible to customers and to the public
 * discovery feed. Until this list existed the UI never sent the field, so every
 * chef-created dish was born hidden — see the form in MenuBuilderPage.
 *
 * Scope is deliberately Nigeria-only (36 states + the FCT) with each state's
 * principal cities, which is the whole delivery footprint today. A wider
 * gazetteer (GeoNames-style, with coordinates and admin hierarchies) is the
 * right answer once the footprint crosses a border; this stays a small curated
 * table so it can be read and corrected by hand.
 *
 * Stored form is `"City, State"` (e.g. `"Abuja, FCT"`), free-form TEXT in the
 * column, so the vocabulary can grow without a migration.
 */

export interface NigerianState {
  /** State name as stored in the tag's second half. */
  name: string
  /** Geopolitical zone — kept for future regional-cuisine grouping. */
  zone: 'North Central' | 'North East' | 'North West' | 'South East' | 'South South' | 'South West'
  /** Principal cities, capital first. */
  cities: readonly string[]
}

export const NIGERIAN_STATES: readonly NigerianState[] = [
  { name: 'FCT', zone: 'North Central', cities: ['Abuja', 'Gwagwalada', 'Kuje', 'Bwari', 'Kubwa', 'Nyanya'] },
  { name: 'Abia', zone: 'South East', cities: ['Umuahia', 'Aba', 'Ohafia', 'Arochukwu'] },
  { name: 'Adamawa', zone: 'North East', cities: ['Yola', 'Mubi', 'Numan', 'Ganye'] },
  { name: 'Akwa Ibom', zone: 'South South', cities: ['Uyo', 'Eket', 'Ikot Ekpene', 'Oron'] },
  { name: 'Anambra', zone: 'South East', cities: ['Awka', 'Onitsha', 'Nnewi', 'Ekwulobia'] },
  { name: 'Bauchi', zone: 'North East', cities: ['Bauchi', 'Azare', 'Misau', 'Jama’are'] },
  { name: 'Bayelsa', zone: 'South South', cities: ['Yenagoa', 'Ogbia', 'Sagbama', 'Brass'] },
  { name: 'Benue', zone: 'North Central', cities: ['Makurdi', 'Otukpo', 'Gboko', 'Katsina-Ala'] },
  { name: 'Borno', zone: 'North East', cities: ['Maiduguri', 'Biu', 'Bama', 'Dikwa'] },
  { name: 'Cross River', zone: 'South South', cities: ['Calabar', 'Ugep', 'Ikom', 'Ogoja'] },
  { name: 'Delta', zone: 'South South', cities: ['Asaba', 'Warri', 'Sapele', 'Ughelli', 'Agbor'] },
  { name: 'Ebonyi', zone: 'South East', cities: ['Abakaliki', 'Afikpo', 'Onueke'] },
  { name: 'Edo', zone: 'South South', cities: ['Benin City', 'Auchi', 'Ekpoma', 'Uromi'] },
  { name: 'Ekiti', zone: 'South West', cities: ['Ado-Ekiti', 'Ikere-Ekiti', 'Ikole-Ekiti', 'Oye-Ekiti'] },
  { name: 'Enugu', zone: 'South East', cities: ['Enugu', 'Nsukka', 'Agbani', 'Udi'] },
  { name: 'Gombe', zone: 'North East', cities: ['Gombe', 'Kumo', 'Billiri', 'Dukku'] },
  { name: 'Imo', zone: 'South East', cities: ['Owerri', 'Orlu', 'Okigwe', 'Mbaise'] },
  { name: 'Jigawa', zone: 'North West', cities: ['Dutse', 'Hadejia', 'Gumel', 'Birnin Kudu'] },
  { name: 'Kaduna', zone: 'North West', cities: ['Kaduna', 'Zaria', 'Kafanchan', 'Zonkwa'] },
  { name: 'Kano', zone: 'North West', cities: ['Kano', 'Wudil', 'Gaya', 'Rano'] },
  { name: 'Katsina', zone: 'North West', cities: ['Katsina', 'Daura', 'Funtua', 'Malumfashi'] },
  { name: 'Kebbi', zone: 'North West', cities: ['Birnin Kebbi', 'Argungu', 'Yauri', 'Zuru'] },
  { name: 'Kogi', zone: 'North Central', cities: ['Lokoja', 'Okene', 'Idah', 'Kabba'] },
  { name: 'Kwara', zone: 'North Central', cities: ['Ilorin', 'Offa', 'Omu-Aran', 'Jebba'] },
  {
    name: 'Lagos',
    zone: 'South West',
    cities: ['Ikeja', 'Lekki', 'Victoria Island', 'Ikoyi', 'Yaba', 'Surulere', 'Ajah', 'Ikorodu', 'Badagry', 'Epe'],
  },
  { name: 'Nasarawa', zone: 'North Central', cities: ['Lafia', 'Keffi', 'Akwanga', 'Karu'] },
  { name: 'Niger', zone: 'North Central', cities: ['Minna', 'Bida', 'Suleja', 'Kontagora'] },
  { name: 'Ogun', zone: 'South West', cities: ['Abeokuta', 'Ijebu-Ode', 'Sagamu', 'Ota', 'Ilaro'] },
  { name: 'Ondo', zone: 'South West', cities: ['Akure', 'Ondo', 'Owo', 'Ikare'] },
  { name: 'Osun', zone: 'South West', cities: ['Osogbo', 'Ile-Ife', 'Ilesa', 'Ede'] },
  { name: 'Oyo', zone: 'South West', cities: ['Ibadan', 'Ogbomoso', 'Oyo', 'Iseyin', 'Saki'] },
  { name: 'Plateau', zone: 'North Central', cities: ['Jos', 'Bukuru', 'Pankshin', 'Shendam'] },
  { name: 'Rivers', zone: 'South South', cities: ['Port Harcourt', 'Bonny', 'Okrika', 'Omoku'] },
  { name: 'Sokoto', zone: 'North West', cities: ['Sokoto', 'Tambuwal', 'Illela', 'Gwadabawa'] },
  { name: 'Taraba', zone: 'North East', cities: ['Jalingo', 'Wukari', 'Bali', 'Takum'] },
  { name: 'Yobe', zone: 'North East', cities: ['Damaturu', 'Potiskum', 'Gashua', 'Nguru'] },
  { name: 'Zamfara', zone: 'North West', cities: ['Gusau', 'Kaura Namoda', 'Talata Mafara', 'Anka'] },
]

/** The pilot's home market — used whenever nothing better can be inferred. */
export const DEFAULT_STATE = 'FCT'
export const DEFAULT_CITY = 'Abuja'
export const DEFAULT_REGION_TAG = `${DEFAULT_CITY}, ${DEFAULT_STATE}`

const STATE_BY_NAME = new Map(NIGERIAN_STATES.map((s) => [s.name.toLowerCase(), s]))

export const citiesForState = (stateName: string): readonly string[] =>
  STATE_BY_NAME.get(stateName.trim().toLowerCase())?.cities ?? []

/** Compose the stored tag. A city outside the curated list is kept verbatim. */
export const composeRegionTag = (city: string, state: string): string => {
  const c = city.trim()
  const s = state.trim() || DEFAULT_STATE
  return c ? `${c}, ${s}` : s
}

/** Split a stored tag back into its parts for editing. */
export const parseRegionTag = (tag: string): { city: string; state: string } => {
  const [head, tail] = String(tag ?? '').split(',')
  const city = (head ?? '').trim()
  const state = (tail ?? '').trim()
  if (state && STATE_BY_NAME.has(state.toLowerCase())) return { city, state }
  // A single-part tag is a state on its own ("Lagos") or an unknown string.
  if (city && STATE_BY_NAME.has(city.toLowerCase())) {
    const known = STATE_BY_NAME.get(city.toLowerCase())!
    return { city: known.cities[0], state: known.name }
  }
  return { city: '', state: '' }
}

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Infer where a chef cooks from whatever free text the app already holds — the
 * vendor's `location_label` ("Lekki Phase 1, Lagos"), their profile city, or a
 * previously saved tag. Matching is longest-name-first so "Ile-Ife" wins over
 * "Ife" and a city hit implies its state. Falls back to the pilot default so
 * the form is always pre-filled with something publishable.
 */
export const inferRegion = (...hints: readonly (string | undefined | null)[]): { city: string; state: string } => {
  for (const hint of hints) {
    const text = String(hint ?? '').trim()
    if (!text) continue
    const haystack = text.toLowerCase()

    // A curated city always wins, and implies its state — "Lekki Phase 1,
    // Lagos" resolves to the selectable pair (Lekki, Lagos) rather than to the
    // verbatim area name, which no dropdown could show as chosen.
    let best: { city: string; state: string; length: number } | undefined
    for (const state of NIGERIAN_STATES) {
      for (const city of state.cities) {
        if (haystack.includes(city.toLowerCase()) && (best?.length ?? 0) < city.length) {
          best = { city, state: state.name, length: city.length }
        }
      }
    }
    if (best) return { city: best.city, state: best.state }

    // No city hit: a named state falls back to its capital. Matched on word
    // boundaries so "Nigeria" is not read as the state of Niger.
    for (const state of NIGERIAN_STATES) {
      if (new RegExp(`\\b${escapeForRegExp(state.name.toLowerCase())}\\b`).test(haystack)) {
        return { city: state.cities[0], state: state.name }
      }
    }
  }
  return { city: DEFAULT_CITY, state: DEFAULT_STATE }
}
